import React from 'react';
import './OnboardingProgress.css';

const OnboardingProgress = ({
  steps = [],
  currentStep = 0,
  completedSteps = [],
  skippedSteps = [],
  onStepClick,
  showLabels = true,
  orientation = 'horizontal', // 'horizontal' | 'vertical'
  size = 'medium', // 'small' | 'medium' | 'large'
  className = '',
  animated = true
}) => {
  const getStepStatus = (stepIndex, stepId) => {
    if (completedSteps.includes(stepId)) return 'completed';
    if (skippedSteps.includes(stepId)) return 'skipped';
    if (stepIndex === currentStep) return 'current';
    if (stepIndex < currentStep) return 'accessible';
    return 'upcoming';
  };

  const getStepIcon = (status, stepIndex) => {
    switch (status) {
      case 'completed':
        return '✓';
      case 'skipped':
        return '⤴';
      case 'current':
        return stepIndex + 1;
      default:
        return stepIndex + 1;
    }
  };

  const getConnectorStatus = (currentIndex) => {
    const currentStepId = steps[currentIndex]?.id || steps[currentIndex];
    
    // Connector is completed if current step is completed
    if (completedSteps.includes(currentStepId)) {
      return 'completed';
    }
    
    // Connector is active if we're currently on this step and progressing
    if (currentIndex === currentStep) {
      return 'active';
    }
    
    return 'default';
  };

  const handleStepClick = (stepIndex, stepId, status) => {
    if (onStepClick && (status === 'completed' || status === 'accessible' || status === 'current')) {
      onStepClick(stepIndex, stepId);
    }
  };

  const calculateProgress = () => {
    const totalSteps = steps.length;
    if (totalSteps === 0) return 0;
    
    const completedCount = completedSteps.length;
    const currentProgress = currentStep > completedCount ? 1 : 0;
    return Math.round(((completedCount + currentProgress) / totalSteps) * 100);
  };

  const progressPercentage = calculateProgress();

  const stepCount = Math.max(steps.length, 1);
  
  // Calculate proper track positioning for horizontal layout
  // In CSS grid with equal columns: first center = 50/N%, last center = (100 - 50/N)%
  // So track runs from 50/N% to 50/N% from each edge
  const trackOffset = stepCount > 1 ? `${50 / stepCount}%` : '50%';
  const trackLeftOffset = trackOffset;
  const trackRightOffset = trackOffset;
  
  return (
    <div 
      className={`onboarding-progress ${orientation} ${size} ${className} ${animated ? 'animated' : ''}`}
      role="progressbar"
      aria-valuenow={currentStep + 1}
      aria-valuemin={1}
      aria-valuemax={steps.length}
      aria-label={`Onboarding progress: ${progressPercentage}% complete`}
      style={{
        '--step-count': stepCount,
        '--track-left-offset': trackLeftOffset,
        '--track-right-offset': trackRightOffset
      }}
    >

      {/* Steps */}
      <div className="progress-steps">
        {steps.map((step, index) => {
          const status = getStepStatus(index, step.id || step);
          const isClickable = status === 'completed' || status === 'accessible' || status === 'current';
          const stepId = step.id || step;
          const stepLabel = step.label || step.title || `Step ${index + 1}`;
          const stepDescription = step.description || '';

          return (
            <div
              key={stepId}
              className={`progress-step ${status} ${isClickable ? 'clickable' : ''}`}
              onClick={() => handleStepClick(index, stepId, status)}
              role={isClickable ? 'button' : 'presentation'}
              tabIndex={isClickable ? 0 : -1}
              aria-label={`${stepLabel}: ${status}${stepDescription ? `. ${stepDescription}` : ''}`}
              onKeyDown={(e) => {
                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleStepClick(index, stepId, status);
                }
              }}
            >
              {/* Step Circle */}
              <div className="step-circle">
                <span className="step-icon" aria-hidden="true">
                  {getStepIcon(status, index)}
                </span>
              </div>

              {/* Step Content */}
              {showLabels && (
                <div className="step-content">
                  <div className="step-label">{stepLabel}</div>
                </div>
              )}

              {/* Connection Line */}
              {index < steps.length - 1 && (
                <div 
                  className={`step-connector ${getConnectorStatus(index)}`} 
                  aria-hidden="true" 
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Summary Information */}
      <div className="progress-summary" aria-live="polite">
        <span className="sr-only">
          {progressPercentage}% complete. 
          {completedSteps.length} of {steps.length} steps completed.
          {skippedSteps.length > 0 && ` ${skippedSteps.length} steps skipped.`}
        </span>
      </div>
    </div>
  );
};

export default OnboardingProgress;
